import React, { useCallback, useEffect, useState } from 'react';
import { Trash2, Lock, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const [pin, setPin] = useState(sessionStorage.getItem('jw_admin_pin') || '');
  const [unlocked, setUnlocked] = useState(false);
  const [dates, setDates] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const loadDates = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/blocked-dates`);
      const data = await res.json();
      setDates(data.dates || []);
    } catch {
      setError('Could not load blocked dates.');
    }
  }, []);

  const unlock = async (event) => {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/api/blocked-dates/verify`, { method: 'POST', headers: { 'X-Admin-Pin': pin } });
      if (!res.ok) throw new Error('bad pin');
      sessionStorage.setItem('jw_admin_pin', pin);
      setUnlocked(true);
      await loadDates();
    } catch {
      setError('Incorrect PIN. Please try again.');
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (sessionStorage.getItem('jw_admin_pin')) {
      setPin(sessionStorage.getItem('jw_admin_pin'));
      fetch(`${API}/api/blocked-dates/verify`, { method: 'POST', headers: { 'X-Admin-Pin': sessionStorage.getItem('jw_admin_pin') } })
        .then((res) => { if (res.ok) { setUnlocked(true); loadDates(); } })
        .catch(() => {});
    }
  }, [loadDates]);

  const addDate = async () => {
    if (!newDate) return;
    setBusy(true); setError('');
    try {
      const res = await fetch(`${API}/api/blocked-dates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Pin': pin },
        body: JSON.stringify({ date: newDate }),
      });
      if (!res.ok) throw new Error('failed');
      setNewDate('');
      await loadDates();
    } catch {
      setError('Could not block that date.');
    } finally { setBusy(false); }
  };

  const removeDate = async (date) => {
    setBusy(true); setError('');
    try {
      await fetch(`${API}/api/blocked-dates/${date}`, { method: 'DELETE', headers: { 'X-Admin-Pin': pin } });
      await loadDates();
    } catch {
      setError('Could not remove that date.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-2">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-red-500">JackWash Owner</span>
          <h1 className="font-heading font-black text-3xl uppercase tracking-tight" data-testid="admin-title">Availability Manager</h1>
          <p className="text-sm text-neutral-400">Block dates you are unavailable. Customers won&apos;t be able to select them in the booking form.</p>
        </div>

        {!unlocked ? (
          <form onSubmit={unlock} className="border border-neutral-800 bg-[#121212] p-6 space-y-4" data-testid="admin-login-form">
            <label className="text-xs font-bold uppercase text-neutral-300 flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-yellow-500" /> Owner PIN</label>
            <Input type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Enter PIN" className="rounded-none border-neutral-800 bg-neutral-900 text-white" data-testid="admin-pin-input" />
            <Button type="submit" disabled={busy || !pin} className="w-full rounded-none bg-red-600 uppercase" data-testid="admin-unlock-btn">{busy ? 'Checking…' : 'Unlock'}</Button>
            {error && <p className="text-sm text-red-400" data-testid="admin-error">{error}</p>}
          </form>
        ) : (
          <div className="border border-neutral-800 bg-[#121212] p-6 space-y-6" data-testid="admin-panel">
            <div className="flex gap-3">
              <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} className="rounded-none border-neutral-800 bg-neutral-900 text-white [color-scheme:dark]" data-testid="admin-add-date-input" />
              <Button onClick={addDate} disabled={busy || !newDate} className="rounded-none bg-red-600 uppercase whitespace-nowrap" data-testid="admin-add-date-btn"><Plus className="w-4 h-4" /> Block Date</Button>
            </div>
            {error && <p className="text-sm text-red-400" data-testid="admin-error">{error}</p>}
            <div className="space-y-2" data-testid="admin-blocked-list">
              {dates.length === 0 && <p className="text-sm text-neutral-500">No dates blocked — every day is bookable.</p>}
              {dates.map((date) => (
                <div key={date} className="flex items-center justify-between border border-neutral-800 bg-neutral-900 px-4 py-3" data-testid={`admin-blocked-date-${date}`}>
                  <span className="text-sm font-semibold">{new Date(`${date}T00:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  <button onClick={() => removeDate(date)} disabled={busy} className="text-neutral-500 hover:text-red-500 transition-colors" data-testid={`admin-remove-${date}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <a href="/" className="block text-center text-xs uppercase tracking-wider text-neutral-500 hover:text-yellow-400" data-testid="admin-back-home">← Back to website</a>
          </div>
        )}
      </div>
    </div>
  );
}
